/**
 * What the buttons on a block actually do.
 *
 * `orange-blocked-state` emits an action id and nothing more, because the component
 * cannot know whether it is standing in for a guard, a rejected number or an empty
 * stock check. Two callers need the eligibility-chain answers — S-01, which renders
 * its own block, and `orange-shell`, which renders the ones the guards resolve — so
 * the mapping lives here instead of being written twice and drifting.
 *
 * ### Five of the eight have no destination
 *
 * `request-sales-contact`, `contact-business`, `who-is-admin`, `view-business-plans`
 * and `view-consumer-offers` all point outside this POC: there is no assisted-sales
 * handoff, no account-administration page and no consumer eShop here, and no source
 * defines what any of them would do (Q-14). They return `'unhandled'` rather than
 * being wired to a plausible page — a button that silently goes nowhere is a visible
 * gap, and a button that goes to an invented page is an invented requirement.
 *
 * The caller decides what to do with `'unhandled'`. Screens do nothing, which matches
 * the prototype — but the press is recorded through `recordUnhandledAction`, so the
 * dev overlay's Diagnostic tab names the gap. That is where an unimplemented action
 * belongs: on the panel whose whole purpose is saying what is unimplemented, not in
 * product UI that would have to invent a destination to be honest about.
 *
 * Recording happens here rather than at the three call sites so none of them can
 * forget, and so the function's signature stays the same for all of them.
 */
import type { Router } from '@angular/router';
import { recordUnhandledAction } from '../dev/unhandled-actions';
import type { SessionStore } from '../stores/session.store';

export type BlockActionResult = 'handled' | 'unhandled';

interface Deps {
  readonly router: Router;
  readonly session: SessionStore;
  /** The URL the block is standing on, so `retry` can re-run the guard that set it. */
  readonly url: string;
}

export function runBlockAction(action: string, deps: Deps): BlockActionResult {
  switch (action) {
    case 'sign-in':
      void deps.router.navigateByUrl('/autentificare');
      return 'handled';

    case 'view-my-orders':
      void deps.router.navigateByUrl('/comenzile-mele');
      return 'handled';

    case 'retry':
      // Re-runs the whole nine-check chain, which is the only thing "retry" can
      // mean on a block that came out of it. Whether a customer should be able to
      // re-trigger a third-party registry lookup by clicking a button — and how
      // often — is part of Q-14, which asks when each rule is evaluated at all.
      //
      // Then re-navigates to the same URL so the guard runs against the new answer.
      // Without that the block outlives the thing that caused it: the store keys a
      // block to the URL a guard resolved it for, and only a guard clears it.
      void deps.session
        .reload()
        .then(() => deps.router.navigateByUrl(deps.url, { onSameUrlNavigation: 'reload' }));
      return 'handled';

    default:
      recordUnhandledAction(action, deps.url);
      return 'unhandled';
  }
}
